import React, { FC, useEffect, useMemo } from "react";
import { CustomDrawer } from "../../atoms/custom-drawer/custom-drawer";
import * as Yup from "yup";
import {
  Box,
  CircularProgress,
  Typography,
  Divider,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { CustomButton, CustomTextBox } from "../../atoms";
import { Colors } from "../../../configs";
import CustomDateTimePicker from "../../atoms/custom-date-time-picker/custom-date-time-picker";
import dayjs from "dayjs";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, Resolver, useForm } from "react-hook-form";
import { CustomCheckBox } from "../../atoms/checkbox/custom-checkbox";
import { MultiTabSelector } from "../../atoms/multi-tab-selector/multi-tab-selector";
import { useAppDispatch } from "../../../hooks";
import { useAppSelector } from "../../../hooks";
import {
  addStoreConfig,
  getStoreConfig,
} from "../../../redux/actions/storeActions";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";
import { storeSliceActions } from "../../../redux/slice/storeSlice";
import { StoreClosureDates } from "../store-closure-dates/StoreClosureDates";
import moment from "moment-timezone";
import {
  DEFAULT_READY_DATE_RULES,
  IReadyDateRule,
  READY_DATE_MODE_DAYS,
  READY_DATE_MODE_TABLE,
  withDefaultRows,
} from "../../../configs/readyDateTable";
import { ReadyDateTableEditor } from "./ReadyDateTableEditor";

interface IEditPickupTimeDrawerProps {
  open: boolean;
  toggleDrawer: () => void;
  storeId: number;
  merchantId: number;
}

const schema = Yup.object().shape({
  storeId: Yup.number().required("Store ID is required"),
  merchantId: Yup.number().required("Merchant ID is required"),

  pickupTime: Yup.string().required("General Pickup Time is required"),
  // .matches(/^([0-1]\d|2[0-3]):([0-5]\d)$/, "Invalid time format"),
  cutOffTime: Yup.string().required("General Check-in Time is required"),
  // .matches(/^([0-1]\d|2[0-3]):([0-5]\d)$/, "Invalid time format"),

  specialDaysToDeliver: Yup.number().required(
    "Special Days to Process is required"
  ),
  daysToDeliver: Yup.number().required("Special Days to Process is required"),
  workingDays: Yup.array().of(
    Yup.string().oneOf([
      "MONDAY",
      "TUESDAY",
      "WEDNESDAY",
      "THURSDAY",
      "FRIDAY",
      "SATURDAY",
      "SUNDAY",
    ])
  ),
  autoCloseEnabled: Yup.boolean().default(false),
  autoCloseTime: Yup.string()
    .nullable()
    .when("autoCloseEnabled", {
      is: true,
      then: (schema) => schema.required("Auto close time is required"),
      otherwise: (schema) => schema,
    }),
  autoCloseTimezone: Yup.string()
    .nullable()
    .when("autoCloseEnabled", {
      is: true,
      then: (schema) => schema.required("Auto close timezone is required"),
      otherwise: (schema) => schema,
    }),
  autoCloseGraceMinutes: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" || originalValue === null ? 0 : value
    )
    .min(0, "Grace period cannot be negative")
    .max(1440, "Grace period cannot exceed 1440 minutes")
    .default(0),
  // Ready dates come from days to process OR the weekly table, never both
  readyDateMode: Yup.string()
    .oneOf([READY_DATE_MODE_DAYS, READY_DATE_MODE_TABLE])
    .default(READY_DATE_MODE_DAYS),
  readyDateRules: Yup.array().default([]),
});

type FormData = Yup.InferType<typeof schema>;
const resolver: Resolver<FormData> = yupResolver(schema);

const EditPickupTimeDrawer: FC<IEditPickupTimeDrawerProps> = ({
  open,
  toggleDrawer,
  storeId,
  merchantId,
}) => {
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: resolver,
    defaultValues: {
      autoCloseEnabled: false,
      autoCloseTime: "00:00",
      autoCloseTimezone: "America/New_York",
      autoCloseGraceMinutes: 0,
      readyDateMode: READY_DATE_MODE_DAYS,
      readyDateRules: DEFAULT_READY_DATE_RULES,
    },
  });
  const dispatch = useAppDispatch();
  const { getStoreConfigResponse } = useAppSelector((state) => state.store);
  //   const getStoreConfigResponse = null;
  const { addStoreConfigResponse, addStoreConfigLoading, addStoreConfigError } =
    useAppSelector((state) => state.store);
  const { showSnackbar } = useSnackbarContext();
  const timezoneOptions = useMemo(() => moment.tz.names(), []);
  const autoCloseEnabled = watch("autoCloseEnabled", false);
  const readyDateMode = watch("readyDateMode", READY_DATE_MODE_DAYS);

  const handleFormSubmit = (data: FormData) => {
    dispatch(
      addStoreConfig({
        storeId: data?.storeId,
        merchantId: data?.merchantId,
        pickupTime: data?.pickupTime,
        cutOffTime: data?.cutOffTime,
        specialDaysToDeliver: data?.specialDaysToDeliver,
        daysToDeliver: data?.daysToDeliver,
        workingDays: data?.workingDays as string[],
        autoCloseEnabled: data?.autoCloseEnabled ?? false,
        autoCloseTime: data?.autoCloseTime ?? "00:00",
        autoCloseTimezone:
          data?.autoCloseTimezone ?? "America/New_York",
        autoCloseGraceMinutes: Number(data?.autoCloseGraceMinutes ?? 0),
        readyDateMode:
          data?.readyDateMode === READY_DATE_MODE_TABLE
            ? READY_DATE_MODE_TABLE
            : READY_DATE_MODE_DAYS,
        readyDateRules: withDefaultRows(data?.readyDateRules as IReadyDateRule[]),
      })
    );
  };

  useEffect(() => {
    if (Object.keys(errors).length) {
      const firstErrorKey = Object.keys(errors)[0] as keyof typeof errors;
      showSnackbar(`Error: ${errors[firstErrorKey]?.message}`, "warning");
    }
  }, [errors]);

  useEffect(() => {
    if (storeId && merchantId) {
      dispatch(getStoreConfig({ storeId, merchantId }));
    }
  }, [storeId, merchantId, dispatch]);

  useEffect(() => {
    if (getStoreConfigResponse && getStoreConfigResponse?.body !== null) {
      setValue("storeId", getStoreConfigResponse.body?.storeId);
      setValue("merchantId", getStoreConfigResponse?.body?.merchantId);
      setValue("pickupTime", getStoreConfigResponse?.body?.pickupTime);
      setValue("cutOffTime", getStoreConfigResponse?.body?.cutOffTime);
      setValue(
        "specialDaysToDeliver",
        getStoreConfigResponse?.body?.specialDaysToDeliver
      );
      setValue("daysToDeliver", getStoreConfigResponse?.body?.daysToDeliver);
      setValue("workingDays", getStoreConfigResponse?.body?.workingDays);
      setValue(
        "autoCloseEnabled",
        getStoreConfigResponse?.body?.autoCloseEnabled ?? false
      );
      setValue(
        "autoCloseTime",
        getStoreConfigResponse?.body?.autoCloseTime ?? "00:00"
      );
      setValue(
        "autoCloseTimezone",
        getStoreConfigResponse?.body?.autoCloseTimezone ??
          "America/New_York"
      );
      setValue(
        "autoCloseGraceMinutes",
        getStoreConfigResponse?.body?.autoCloseGraceMinutes ?? 0
      );
      setValue(
        "readyDateMode",
        getStoreConfigResponse?.body?.readyDateMode ?? READY_DATE_MODE_DAYS
      );
      setValue(
        "readyDateRules",
        withDefaultRows(getStoreConfigResponse?.body?.readyDateRules)
      );
    } else {
      setValue("storeId", storeId);
      setValue("merchantId", merchantId);
      setValue("pickupTime", "15:00:00");
      setValue("cutOffTime", "10:00:00");
      setValue("specialDaysToDeliver", 0);
      setValue("daysToDeliver", 2);
      setValue("workingDays", [
        "MONDAY",
        "TUESDAY",
        "WEDNESDAY",
        "THURSDAY",
        "FRIDAY",
      ]);
      setValue("autoCloseEnabled", false);
      setValue("autoCloseTime", "00:00");
      setValue("autoCloseTimezone", "America/New_York");
      setValue("autoCloseGraceMinutes", 0);
      setValue("readyDateMode", READY_DATE_MODE_DAYS);
      setValue("readyDateRules", DEFAULT_READY_DATE_RULES);
    }
  }, [getStoreConfigResponse, merchantId, setValue, storeId]);

  // useEffect(() => {
  //   if (getStoreConfigResponse) {

  //   } else {
  //     if (storeId && merchantId) {
  //       dispatch(getStoreConfig({ storeId, merchantId }));
  //     } else {
  //       showSnackbar("Store ID or Merchant ID is missing", "error");
  //     }
  //   }
  // }, [getStoreConfigResponse, storeId, merchantId]);

  // useEffect(() => {
  //   if (getStoreConfigResponse) {
  //   } else {
  //   }
  // }, [getStoreConfigResponse, storeId, merchantId]);

  useEffect(() => {
    if (addStoreConfigError) {
      showSnackbar(addStoreConfigError, "error");
    }
  }, [addStoreConfigError]);

  useEffect(() => {
    if (addStoreConfigResponse) {
      showSnackbar("Store config updated successfully", "success");
      dispatch(storeSliceActions.resetStoreConfig());
      toggleDrawer();
    }
  }, [addStoreConfigResponse]);

  return (
    <CustomDrawer
      isOpen={open}
      toggleDrawer={() => {
        dispatch(storeSliceActions.resetStoreConfig());
        toggleDrawer();
      }}
      // Wider for the weekly table
      sx={{
        width:
          readyDateMode === READY_DATE_MODE_TABLE ? "64rem" : "34.2857143rem",
      }}
    >
      <form
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "3.5rem",
          padding: "3rem",
        }}
        onSubmit={handleSubmit((data) => {
          handleFormSubmit(data);
        })}
      >
        <Controller
          name="storeId"
          control={control}
          defaultValue={storeId}
          render={({ field }) => (
            <input type="hidden" readOnly disabled {...field} />
          )}
        />
        <Controller
          name="merchantId"
          control={control}
          defaultValue={merchantId}
          render={({ field }) => (
            <input type="hidden" readOnly disabled {...field} />
          )}
        />
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Store Pickup Time Config
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "3rem" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <Box sx={{ display: "flex", gap: "1rem", alignItems: "center" }}>
              <Typography variant="body1" sx={{ width: "12.5rem" }}>
                Check-in Cut-off Time:
              </Typography>
              <Controller
                name="cutOffTime"
                control={control}
                render={({ field }) => (
                  <CustomDateTimePicker
                    type="time"
                    value={field.value ? dayjs(field.value, "HH:mm:ss") : null} // Convert string to Dayjs
                    onChange={(date) =>
                      field.onChange(date ? date.format("HH:mm:ss") : "")
                    } // Convert Dayjs back to string
                    sx={{ width: "10rem" }}
                  />
                )}
              />
            </Box>
            <Box sx={{ display: "flex", gap: "1rem", alignItems: "center" }}>
              <Typography variant="body1" sx={{ width: "12.5rem" }}>
                Pickup Time:
              </Typography>
              <Controller
                name="pickupTime"
                control={control}
                render={({ field }) => (
                  <CustomDateTimePicker
                    type="time"
                    value={field.value ? dayjs(field.value, "HH:mm:ss") : null} // Convert string to Dayjs
                    onChange={(date) =>
                      field.onChange(date ? date.format("HH:mm:ss") : "")
                    } // Convert Dayjs back to string
                    sx={{ width: "10rem" }}
                  />
                )}
              />
            </Box>
            <Box
              sx={{ display: "flex", gap: "1rem", alignItems: "flex-start" }}
            >
              <Typography variant="body1" sx={{ width: "12.5rem" }}>
                Working Days:
              </Typography>
              <Controller
                name="workingDays"
                control={control}
                render={({ field, fieldState }) => (
                  <MultiTabSelector
                    items={[
                      { value: "MONDAY", label: "Monday" },
                      { value: "TUESDAY", label: "Tuesday" },
                      { value: "WEDNESDAY", label: "Wednesday" },
                      { value: "THURSDAY", label: "Thursday" },
                      { value: "FRIDAY", label: "Friday" },
                      { value: "SATURDAY", label: "Saturday" },
                      { value: "SUNDAY", label: "Sunday" },
                    ]}
                    value={field.value as string[]}
                    mode="multiple"
                    layout="vertical"
                    onChange={(values) => field.onChange(values)}
                  />
                )}
              />
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              Ready Dates:
            </Typography>
            <Controller
              name="readyDateMode"
              control={control}
              render={({ field }) => (
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  color="primary"
                  aria-label="Ready date mode"
                  value={field.value ?? READY_DATE_MODE_DAYS}
                  // Clicking the active mode again keeps it (one is always on)
                  onChange={(_, mode) => mode && field.onChange(mode)}
                >
                  <ToggleButton
                    value={READY_DATE_MODE_DAYS}
                    sx={{ fontSize: "var(--fs-2xs)", textTransform: "none", px: "1.5rem" }}
                  >
                    Days to Process
                  </ToggleButton>
                  <ToggleButton
                    value={READY_DATE_MODE_TABLE}
                    sx={{ fontSize: "var(--fs-2xs)", textTransform: "none", px: "1.5rem" }}
                  >
                    Weekly Table
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            />
            <Typography
              sx={{ fontSize: "var(--fs-2xs)", color: Colors.TEXT_GRAY_DARK }}
            >
              {readyDateMode === READY_DATE_MODE_TABLE
                ? "Special and Regular ready days come from the table below, using the cut-off and pickup times above."
                : "Special and Regular ready dates are the days to process below, counting working days."}
            </Typography>
          </Box>
          {readyDateMode === READY_DATE_MODE_TABLE ? (
            <Controller
              name="readyDateRules"
              control={control}
              render={({ field }) => (
                <ReadyDateTableEditor
                  rules={withDefaultRows(field.value as IReadyDateRule[])}
                  onChange={field.onChange}
                />
              )}
            />
          ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              Days to Process:
            </Typography>
            <Box sx={{ display: "flex", gap: "1rem", alignItems: "center" }}>
              <Typography variant="body1" sx={{ width: "12.5rem" }}>
                General Service:
              </Typography>
              <Controller
                name="daysToDeliver"
                control={control}
                render={({ field }) => (
                  <CustomTextBox
                    type="number"
                    value={field.value}
                    onChange={field.onChange}
                    sx={{ width: "10rem" }}
                  />
                )}
              />
            </Box>
            <Box sx={{ display: "flex", gap: "1rem", alignItems: "center" }}>
              <Typography variant="body1" sx={{ width: "12.5rem" }}>
                Special Service:
              </Typography>
              <Controller
                name="specialDaysToDeliver"
                control={control}
                render={({ field }) => (
                  <CustomTextBox
                    type="number"
                    value={field.value}
                    onChange={field.onChange}
                    sx={{ width: "10rem" }}
                  />
                )}
              />
            </Box>
          </Box>
          )}
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Auto Close Configuration:
          </Typography>
          <Controller
            name="autoCloseEnabled"
            control={control}
            render={({ field }) => (
              <CustomCheckBox
                label="Enable automatic station close-out"
                checked={Boolean(field.value)}
                onChange={(event) => field.onChange(event.target.checked)}
              />
            )}
          />
          <Box sx={{ display: "flex", gap: "1rem", alignItems: "center" }}>
            <Typography variant="body1" sx={{ width: "12.5rem" }}>
              Trigger Time:
            </Typography>
            <Controller
              name="autoCloseTime"
              control={control}
              render={({ field }) => (
                <CustomDateTimePicker
                  type="time"
                  value={
                    field.value ? dayjs(field.value, "HH:mm") : dayjs("00:00", "HH:mm")
                  }
                  onChange={(date) =>
                    field.onChange(date ? date.format("HH:mm") : "00:00")
                  }
                  sx={{ width: "10rem" }}
                  disabled={!autoCloseEnabled}
                />
              )}
            />
          </Box>
          <Box sx={{ display: "flex", gap: "1rem", alignItems: "center" }}>
            <Typography variant="body1" sx={{ width: "12.5rem" }}>
              Time Zone:
            </Typography>
            <Controller
              name="autoCloseTimezone"
              control={control}
              render={({ field }) => (
                <CustomTextBox
                  select
                  value={field.value ?? "America/New_York"}
                  onChange={field.onChange}
                  sx={{ width: "100%" }}
                  disabled={!autoCloseEnabled}
                >
                  {timezoneOptions.map((tz) => (
                    <MenuItem key={tz} value={tz}>
                      {tz}
                    </MenuItem>
                  ))}
                </CustomTextBox>
              )}
            />
          </Box>
          <Box sx={{ display: "flex", gap: "1rem", alignItems: "center" }}>
            <Typography variant="body1" sx={{ width: "12.5rem" }}>
              Grace Period (minutes):
            </Typography>
            <Controller
              name="autoCloseGraceMinutes"
              control={control}
              render={({ field }) => (
                <CustomTextBox
                  type="number"
                  value={field.value ?? 0}
                  onChange={field.onChange}
                  sx={{ width: "10rem" }}
                  disabled={!autoCloseEnabled}
                  inputProps={{ min: 0, max: 1440 }}
                />
              )}
            />
          </Box>
        </Box>
        
        <Divider sx={{ my: 3 }} />
        
        <StoreClosureDates storeId={storeId} merchantId={merchantId} />
        
        <CustomButton
          sx={{ fontSize: "1.5rem", paddingBlock: "0.5rem" }}
          type="submit"
          disabled={addStoreConfigLoading}
        >
          {addStoreConfigLoading ? <CircularProgress /> : "Save Config"}
        </CustomButton>
      </form>
    </CustomDrawer>
  );
};

export { EditPickupTimeDrawer };
